import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Dumbbell, Play, Zap, BarChart3, ChevronRight, SlidersHorizontal, ChevronDown } from "lucide-react";
import { useUserAuth, getUserToken } from "../context/UserAuthContext";
import UserNav from "../components/UserNav";

const API = process.env.REACT_APP_BACKEND_URL || "";

const DIFFICULTY = {
  1: { label: "Iniciación", color: "#4ade80", bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.28)" },
  2: { label: "Intermedio", color: "#F2C230", bg: "rgba(242,194,48,0.10)", border: "rgba(242,194,48,0.30)" },
  3: { label: "Avanzado", color: "#fb923c", bg: "rgba(251,146,60,0.10)", border: "rgba(251,146,60,0.28)" },
};

const DIFICULTAD_COLOR_CLASS = {
  1: "text-green-400 border-green-500/30",
  2: "text-[#F2C230] border-[#F2C230]/30",
  3: "text-orange-400 border-orange-500/30",
};

function fotoSrc(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
}

function getYouTubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&playsinline=1` : null;
}

export default function MachineCatalogPage() {
  const { user, loading, logout } = useUserAuth();
  const navigate = useNavigate();

  const [machines, setMachines] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selected, setSelected] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("");
  const [filterDif, setFilterDif] = useState("");
  const [filterPlanta, setFilterPlanta] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    fetch(`${API}/api/maquinas`)
      .then((r) => r.json())
      .then((d) => setMachines(Array.isArray(d) ? d : []))
      .catch(() => setMachines([]))
      .finally(() => setFetching(false));
  }, [user, loading]);

  const grupos = useMemo(() => {
    const all = new Set();
    machines.forEach((m) => {
      if (m.grupo_muscular) {
        m.grupo_muscular.split(/[·,\/]/).forEach((g) => { const t = g.trim(); if (t) all.add(t); });
      }
    });
    return [...all].sort();
  }, [machines]);
  const plantas = useMemo(() => [...new Set(machines.map((m) => m.planta).filter(Boolean))].sort(), [machines]);

  const filtered = useMemo(() => machines.filter((m) => {
    if (search) {
      const q = search.toLowerCase();
      if (!m.nombre?.toLowerCase().includes(q) && !m.numero?.toLowerCase().includes(q) && !m.grupo_muscular?.toLowerCase().includes(q)) return false;
    }
    if (filterGrupo && !(m.grupo_muscular || "").toLowerCase().includes(filterGrupo.toLowerCase())) return false;
    if (filterDif && String(m.dificultad) !== filterDif) return false;
    if (filterPlanta && m.planta !== filterPlanta) return false;
    return true;
  }), [machines, search, filterGrupo, filterDif, filterPlanta]);

  const hasFilters = search || filterGrupo || filterDif || filterPlanta;
  const clearFilters = () => { setSearch(""); setFilterGrupo(""); setFilterDif(""); setFilterPlanta(""); };
  const handleLogout = () => { logout(); navigate("/login", { replace: true }); };

  if (loading || fetching) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="machine-catalog-page">
      <UserNav user={user} onLogout={handleLogout} />

      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#F2C230] font-black mb-1">Equipamiento</p>
            <h1 className="text-3xl font-black">Nuestras Máquinas</h1>
            <p className="text-white/35 text-sm mt-1">{machines.length} máquina{machines.length !== 1 ? "s" : ""} disponible{machines.length !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters-btn"
            className={`flex items-center gap-1.5 px-3 py-2 border text-[11px] font-black tracking-[0.15em] uppercase transition-colors mt-1 ${showFilters ? "border-[#F2C230] text-[#F2C230]" : "border-white/10 text-white/40 hover:text-white hover:border-white/25"}`}>
            <SlidersHorizontal size={12} /> Filtros
            {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#F2C230]" />}
          </button>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-6 space-y-3" data-testid="filter-panel">
            {/* Search */}
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, número o músculo..."
                data-testid="filter-search"
                className="w-full bg-black border border-white/10 text-white placeholder:text-white/25 pl-8 pr-9 py-2.5 text-sm outline-none focus:border-[#F2C230] transition-colors"
              />
              {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X size={12} /></button>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {/* Grupo muscular */}
              <div className="relative">
                <select value={filterGrupo} onChange={(e) => setFilterGrupo(e.target.value)}
                  data-testid="filter-grupo"
                  className="w-full bg-black border border-white/10 text-sm px-3 py-2.5 outline-none focus:border-[#F2C230] appearance-none transition-colors text-white/70">
                  <option value="">Grupo muscular</option>
                  {grupos.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <ChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
              </div>

              {/* Dificultad */}
              <div className="relative">
                <select value={filterDif} onChange={(e) => setFilterDif(e.target.value)}
                  data-testid="filter-dificultad"
                  className="w-full bg-black border border-white/10 text-sm px-3 py-2.5 outline-none focus:border-[#F2C230] appearance-none transition-colors text-white/70">
                  <option value="">Dificultad</option>
                  <option value="1">Iniciación</option>
                  <option value="2">Intermedio</option>
                  <option value="3">Avanzado</option>
                </select>
                <ChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
              </div>

              {/* Planta */}
              {plantas.length > 1 && (
                <div className="relative">
                  <select value={filterPlanta} onChange={(e) => setFilterPlanta(e.target.value)}
                    data-testid="filter-planta"
                    className="w-full bg-black border border-white/10 text-sm px-3 py-2.5 outline-none focus:border-[#F2C230] appearance-none transition-colors text-white/70">
                    <option value="">Planta</option>
                    {plantas.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                  <ChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                </div>
              )}
            </div>

            {hasFilters && (
              <button onClick={clearFilters} data-testid="clear-filters-btn"
                className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/30 hover:text-white transition-colors font-bold">
                <X size={11} /> Limpiar filtros
              </button>
            )}
          </div>
        )}

        {/* Results count when filtering */}
        {hasFilters && (
          <p className="text-[10px] text-white/30 uppercase tracking-widest mb-4">
            {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
          </p>
        )}

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="border border-white/8 p-14 text-center">
            <Dumbbell size={36} className="mx-auto text-white/10 mb-3" />
            <p className="text-white/35 text-sm">{hasFilters ? "Sin resultados. Prueba con otro filtro." : "No hay máquinas disponibles."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="machine-grid">
            {filtered.map((m) => (
              <MachineCard key={m.numero} machine={m} onClick={() => setSelected(m)} />
            ))}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <MachineDetailPanel key={selected.numero} machine={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

// ---- Machine card ----
function MachineCard({ machine: m, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const src = fotoSrc(m.foto_url);
  const diff = m.dificultad ? DIFFICULTY[m.dificultad] : null;

  return (
    <button onClick={onClick} data-testid={`machine-card-${m.numero}`}
      className="group text-left bg-[#0a0a0a] border border-white/8 hover:border-[#F2C230]/50 transition-all duration-200 overflow-hidden flex flex-col focus:outline-none">
      {/* Photo */}
      <div className="relative aspect-square bg-black overflow-hidden">
        {src && !imgErr ? (
          <img src={src} alt={m.nombre} onError={() => setImgErr(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-white/[0.02]">
            <Dumbbell size={32} className="text-white/10" />
          </div>
        )}
        <span className="absolute top-2 left-2 bg-[#F2C230] text-black font-black text-[10px] px-1.5 py-0.5 tracking-wider leading-none">
          {m.numero}
        </span>
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="text-[10px] font-black tracking-[0.2em] uppercase text-white flex items-center gap-1">
            Ver detalles <ChevronRight size={11} />
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-xs font-black text-white leading-tight line-clamp-2">{m.nombre}</p>
        {m.grupo_muscular && (
          <p className="text-[10px] text-[#F2C230]/70 font-bold uppercase tracking-wider truncate">{m.grupo_muscular}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1">
          {diff ? (
            <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border ${DIFICULTAD_COLOR_CLASS[m.dificultad]}`}>
              {diff.label}
            </span>
          ) : <span />}
          {m.planta && <span className="text-[9px] text-white/25 uppercase tracking-wider">{m.planta}</span>}
        </div>
      </div>
    </button>
  );
}

// ---- Detail panel (right slide-in) ----
function MachineDetailPanel({ machine: m, onClose }) {
  const diff = m.dificultad ? DIFFICULTY[m.dificultad] : null;
  const [imgErr, setImgErr] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [selectedEx, setSelectedEx] = useState(null);
  const src = fotoSrc(m.foto_url);

  const activeExercise = selectedEx;
  const videoUrl = activeExercise?.video_url || m.video_url;
  const instrucciones = activeExercise?.instrucciones_breves || m.instrucciones_breves;
  const embedUrl = getYouTubeEmbed(videoUrl);

  // Lock body scroll while panel is open (fixes mobile scroll competition)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fetch exercises for this machine
  useEffect(() => {
    fetch(`${API}/api/maquinas/${encodeURIComponent(m.numero)}/ejercicios`)
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setExercises(Array.isArray(d) ? d : []))
      .catch(() => setExercises([]));
  }, [m.numero]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="machine-detail-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-md bg-[#0d0d0d] border-l border-white/10 h-full overflow-y-auto overscroll-contain flex flex-col animate-in slide-in-from-right duration-200">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/8 px-5 py-4 flex items-center justify-between">
          <span className="bg-[#F2C230] text-black font-black text-sm tracking-widest px-3 py-1">
            {m.numero}
          </span>
          <button onClick={onClose} data-testid="panel-close-btn"
            className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white border border-white/10 hover:border-white/25 transition-colors">
            <X size={15} />
          </button>
        </div>

        {/* Photo */}
        <div className="w-full aspect-video overflow-hidden bg-black shrink-0">
          {src && !imgErr ? (
            <img src={src} alt={m.nombre} onError={() => setImgErr(true)}
              className="w-full h-full object-cover" data-testid="panel-photo" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-white/[0.02]">
              <Dumbbell size={48} className="text-white/10" />
              <span className="text-white/20 text-xs tracking-widest uppercase">Foto próximamente</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col gap-5 flex-1">
          {/* Name + ref */}
          <div>
            <h2 className="text-2xl font-black leading-tight mb-1" data-testid="panel-name">{m.nombre}</h2>
            {m.modelo && <p className="text-white/30 text-[11px] tracking-[0.2em] uppercase">Ref. {m.modelo}</p>}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2" data-testid="panel-tags">
            {m.grupo_muscular && (
              <Tag icon={<Dumbbell size={11} />} label={m.grupo_muscular} />
            )}
            {m.tipo_esfuerzo && (
              <Tag icon={<Zap size={11} />} label={m.tipo_esfuerzo} />
            )}
            {m.planta && (
              <Tag icon={<BarChart3 size={11} />} label={`Planta ${m.planta}`} />
            )}
            {diff && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black tracking-widest uppercase"
                style={{ color: diff.color, background: diff.bg, border: `1px solid ${diff.border}` }}
                data-testid="panel-difficulty">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: diff.color }} />
                {diff.label}
              </span>
            )}
          </div>

          {/* Instructions */}
          {instrucciones && (
            <div className="p-4 border border-white/8 bg-white/[0.02]" data-testid="panel-instructions">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-2">Cómo usar</p>
              <p className="text-sm text-white/75 leading-relaxed">{instrucciones}</p>
            </div>
          )}

          {/* Exercises list */}
          {exercises.length > 0 && !selectedEx && (
            <div data-testid="panel-exercises">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-3">
                Ejercicios en esta máquina
              </p>
              <div className="space-y-2">
                {exercises.map((ex) => (
                  <button
                    key={ex.id || ex.codigo}
                    onClick={() => setSelectedEx(ex)}
                    data-testid={`panel-exercise-${ex.codigo}`}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 border border-white/10 hover:border-[#F2C230]/50 bg-white/[0.02] hover:bg-white/[0.04] transition-all text-left group"
                  >
                    <div>
                      <p className="font-bold text-sm text-white group-hover:text-[#F2C230] transition-colors">{ex.nombre}</p>
                      {ex.dificultad && (
                        <p className="text-[10px] text-white/35 mt-0.5">{DIFFICULTY[ex.dificultad]?.label}</p>
                      )}
                    </div>
                    <ChevronRight size={15} className="text-white/25 group-hover:text-[#F2C230] shrink-0 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected exercise back button */}
          {selectedEx && (
            <button
              onClick={() => setSelectedEx(null)}
              data-testid="panel-back-exercises"
              className="flex items-center gap-2 text-white/40 hover:text-white text-xs tracking-widest uppercase font-bold transition-colors"
            >
              ← Todos los ejercicios
            </button>
          )}

          {/* Video — show when no exercises OR when one is selected */}
          {(exercises.length === 0 || selectedEx) && (
            embedUrl ? (
              <div className="mx-auto w-full max-w-[260px]" data-testid="panel-video-embed">
                <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 shadow-[0_0_30px_rgba(0,0,0,0.7)]">
                  <iframe src={embedUrl} title="Tutorial"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen className="absolute inset-0 w-full h-full" />
                </div>
                <p className="text-center text-[10px] text-white/25 mt-2 tracking-widest uppercase">Tutorial</p>
              </div>
            ) : videoUrl ? (
              <a href={videoUrl} target="_blank" rel="noopener noreferrer" data-testid="panel-video-btn"
                className="flex items-center justify-center gap-3 bg-[#F2C230] text-black font-black text-sm tracking-widest uppercase py-3.5 hover:bg-white transition-colors">
                <Play size={16} fill="black" /> Ver vídeo tutorial
              </a>
            ) : (
              <div className="flex items-center justify-center gap-2 border border-white/8 py-3.5 text-white/20 text-sm font-bold tracking-wider uppercase" data-testid="panel-video-placeholder">
                <Play size={15} /> Vídeo próximamente
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function Tag({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 text-[11px] text-white/65 font-bold tracking-widest uppercase">
      {icon}{label}
    </span>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="catalog-loading">
      <div className="w-8 h-8 border-2 border-[#F2C230] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
